import { format, isValid } from 'date-fns';
import { Booking, BookingType, FieldConfiguration } from '@/types';

/**
 * Shared logic for resolving which booking-type field configurations appear on
 * the planner booking tooltip and what value each one shows.
 *
 * Field names are matched case-insensitively and trimmed so that configurations
 * whose stored casing differs from the canonical display name (e.g. "status"
 * vs "Status") still resolve correctly.
 */

/** Minimal shape needed to resolve a name from an id-based lookup list. */
export interface NamedEntity {
  id: string | number;
  name: string;
}

export interface TooltipFieldLookups {
  bookingType?: BookingType;
  projectDetails: ReadonlyArray<NamedEntity>;
  clients: ReadonlyArray<NamedEntity>;
  costCentres: ReadonlyArray<NamedEntity>;
}

const normalizeFieldName = (name: string): string => name.trim().toLowerCase();

// Fields rendered in their own dedicated tooltip sections (dates / status badge).
// They must be excluded from the generic dynamic-field list to avoid duplication.
const DEDICATED_TOOLTIP_FIELDS = new Set(['from', 'to', 'status']);

const STANDARD_FIELD_MAP: Record<string, string> = {
  'project': 'projectName',
  'customer': 'customer',
  'description': 'description',
  'location': 'location',
  'details': 'details',
  'booking reference': 'bookingReference',
  'cost centre': 'costCentre',
  'booking notes': 'bookingNotes',
  'task description': 'taskDescription',
  'work type': 'workType',
  'colour': 'color',
  'priority': 'priority',
  'billing classification': 'billingClassification',
};

/**
 * Booking-type field configurations that should render in the generic dynamic
 * section of the tooltip. Includes every field flagged `showTooltip` except the
 * ones handled by dedicated sections (From/To/Status). "Booking Type" is
 * intentionally included so it renders when configured — it is a valid tooltip
 * field (its value resolves to the booking type name).
 */
export function getDynamicTooltipFields(
  fieldConfigurations: FieldConfiguration[]
): FieldConfiguration[] {
  return fieldConfigurations.filter(
    (field) =>
      field.showTooltip === true &&
      !DEDICATED_TOOLTIP_FIELDS.has(normalizeFieldName(field.fieldName))
  );
}

/** Whether a named field is flagged to show on the tooltip (case-insensitive). */
export function isFieldOnTooltip(
  fieldConfigurations: FieldConfiguration[],
  name: string
): boolean {
  const target = normalizeFieldName(name);
  const fc = fieldConfigurations.find((f) => normalizeFieldName(f.fieldName) === target);
  return fc?.showTooltip === true;
}

/** Display label for a field — the configured customName, else the field name. */
export function getTooltipFieldLabel(
  fieldConfigurations: FieldConfiguration[],
  name: string
): string {
  const target = normalizeFieldName(name);
  const fc = fieldConfigurations.find((f) => normalizeFieldName(f.fieldName) === target);
  return fc?.customName || name;
}

/** True when the field is the colour swatch field (rendered specially). */
export function isColourField(fieldName: string): boolean {
  return normalizeFieldName(fieldName) === 'colour';
}

/**
 * Resolve the display value for a configured tooltip field. Returns null when
 * the field has no meaningful value (so the caller can skip rendering it).
 */
export function getTooltipFieldValue(
  booking: Booking,
  fieldName: string,
  lookups: TooltipFieldLookups
): string | null {
  const userCharMatch = fieldName.match(/^User Char (\d+)$/i);
  if (userCharMatch) {
    const index = parseInt(userCharMatch[1], 10);
    return booking.userChar?.[index] || null;
  }

  const userNumberMatch = fieldName.match(/^User Number (\d+)$/i);
  if (userNumberMatch) {
    const index = parseInt(userNumberMatch[1], 10);
    const value = booking.userNumber?.[index];
    if (value === undefined || value === null || value === '') return null;
    return String(value);
  }

  const userFlagMatch = fieldName.match(/^User Flag (\d+)$/i);
  if (userFlagMatch) {
    const index = parseInt(userFlagMatch[1], 10);
    const value = booking.userFlag?.[index];
    if (value === undefined || value === null) return null;
    return value ? 'Yes' : 'No';
  }

  const userDateMatch = fieldName.match(/^User Date (\d+)$/i);
  if (userDateMatch) {
    const index = parseInt(userDateMatch[1], 10);
    const value = booking.userDate?.[index];
    if (!value) return null;
    try {
      const date = new Date(value);
      if (isValid(date)) return format(date, 'PPP');
    } catch {
      return String(value);
    }
    return null;
  }

  const userTimeMatch = fieldName.match(/^User Time (\d+)$/i);
  if (userTimeMatch) {
    const index = parseInt(userTimeMatch[1], 10);
    const value = booking.userTime?.[index];
    if (!value) return null;
    try {
      const time = new Date(value);
      if (isValid(time)) return format(time, 'HH:mm');
    } catch {
      return String(value);
    }
    return null;
  }

  const userNotesMatch = fieldName.match(/^User Notes (\d+)$/i);
  if (userNotesMatch) {
    const index = parseInt(userNotesMatch[1], 10);
    return booking.userNotes?.[index] || null;
  }

  const normalized = normalizeFieldName(fieldName);

  // Booking Type — resolve name from settings
  if (normalized === 'booking type') {
    return lookups.bookingType?.name || null;
  }

  // Cost Centre Task
  if (normalized === 'cost centre task') {
    return booking.costCentreTask || null;
  }

  const bookingField = STANDARD_FIELD_MAP[normalized];
  if (!bookingField) return null;

  let value: unknown = (booking as unknown as Record<string, unknown>)[bookingField];

  // Handle special cases - lookup names from IDs
  if (normalized === 'project' && !value && booking.projectId) {
    const project = lookups.projectDetails.find((p) => String(p.id) === String(booking.projectId));
    value = project?.name;
  }

  if (normalized === 'customer' && !value && booking.customer) {
    const customer = lookups.clients.find((c) => String(c.id) === String(booking.customer));
    value = customer?.name;
  }

  if (normalized === 'cost centre' && !value && booking.costCentreId) {
    const costCentre = lookups.costCentres.find((cc) => String(cc.id) === String(booking.costCentreId));
    value = costCentre?.name;
  }

  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
